import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { useCallback, useEffect, useState } from 'react';

type FingerprintResult = {
	visitorId: string;
	components: Record<string, unknown>;
};

type UseFingerprintReturn = {
	fingerprint: string | null;
	fingerprintData: FingerprintResult | null;
	isLoading: boolean;
	error: Error | null;
	refetch: () => Promise<string>;
};

/**
 * Hook to collect browser fingerprint using FingerprintJS
 * @param autoFetch - Whether to automatically fetch fingerprint on mount (default: true)
 * @returns fingerprint data and loading state
 */
export const useFingerprint = (autoFetch = true): UseFingerprintReturn => {
	const [fingerprint, setFingerprint] = useState<string | null>(null);
	const [fingerprintData, setFingerprintData] =
		useState<FingerprintResult | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<Error | null>(null);

	const fetchFingerprint = useCallback(async () => {
		try {
			setIsLoading(true);
			setError(null);

			// Load the FingerprintJS agent
			const fp = await FingerprintJS.load();

			// Get the visitor identifier
			const result = await fp.get();

			// The visitor identifier
			const visitorId = result.visitorId;

			setFingerprint(visitorId);
			setFingerprintData({
				visitorId,
				components: result.components as Record<string, unknown>,
			});

			// console.log('[Fingerprint] Browser fingerprint collected:', visitorId);

			return visitorId;
		} catch (err) {
			const errorObj =
				err instanceof Error ? err : new Error('Failed to get fingerprint');
			setError(errorObj);
			console.error('[Fingerprint] Error collecting fingerprint:', err);
			throw errorObj;
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		if (autoFetch) {
			fetchFingerprint();
		}
	}, [autoFetch, fetchFingerprint]);

	return {
		fingerprint,
		fingerprintData,
		isLoading,
		error,
		refetch: fetchFingerprint,
	};
};

export default useFingerprint;
